import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  Slider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Star as VipIcon,
  Pending as PendingIcon,
  Verified as VerifiedIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  DriveEta as DriverIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Hotel as HotelIcon,
  Apartment as ApartmentIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  Store as StoreIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  VpnKey as KeyIcon,
  SupervisorAccount as SubAdminIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { FirebaseContext } from 'common';
import { useTranslation } from 'react-i18next';

const TYPE_CONFIG = {
  taxi: { label: 'Taxi', color: '#f59e0b', icon: TaxiIcon },
  rideshare: { label: 'Rideshare', color: '#34D186', icon: CarIcon },
  vip: { label: 'VIP', color: '#6366f1', icon: VipIcon },
};

const STATUS_CONFIG = {
  pending: { label: 'Venter', color: 'warning', icon: PendingIcon },
  approved: { label: 'Godkjent', color: 'success', icon: VerifiedIcon },
  rejected: { label: 'Avvist', color: 'error', icon: RejectIcon },
};

const USERTYPE_CONFIG = {
  customer: { label: 'Kunde', color: '#3b82f6', icon: PersonIcon },
  driver: { label: 'Sjåfør', color: '#22c55e', icon: DriverIcon },
  fleetadmin: { label: 'Flåteadmin', color: '#f59e0b', icon: BusinessIcon },
  admin: { label: 'Admin', color: '#ef4444', icon: AdminIcon },
};

const PARTNER_TYPE_CONFIG = {
  hotel: { label: 'Hotell', color: '#8b5cf6', icon: HotelIcon },
  company: { label: 'Bedrift', color: '#0ea5e9', icon: ApartmentIcon },
  restaurant: { label: 'Restaurant', color: '#f97316', icon: StoreIcon },
  other: { label: 'Annet', color: '#6b7280', icon: BusinessIcon },
};

const PARTNER_USER_ROLE_CONFIG = {
  user: { label: 'Bruker', color: '#3b82f6', icon: PersonIcon, description: 'Kan booke turer for gjester' },
  subadmin: { label: 'Sub-admin', color: '#f59e0b', icon: SubAdminIcon, description: 'Kan se provisjon og justere priser' },
};

// Generate random password
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

export default function TurPrisAdmin() {
  const { t } = useTranslation();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector(state => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [guestBookings, setGuestBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // New user/company/partner dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [createType, setCreateType] = useState('user'); // 'user', 'company', 'partner', or 'partnerUser'
  const [newUserForm, setNewUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    usertype: 'customer',
    approved: true,
  });

  // Partner user creation
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [selectedPartnerForUser, setSelectedPartnerForUser] = useState(null);
  const [newPartnerUserForm, setNewPartnerUserForm] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    role: 'user', // 'user' or 'subadmin'
    partnerId: '',
    active: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    name: '',
    type: 'taxi',
    orgNumber: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    status: 'approved',
  });
  const [newPartnerForm, setNewPartnerForm] = useState({
    name: '',
    type: 'hotel',
    orgNumber: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    // Pricing settings
    pricingType: 'percentage', // 'percentage', 'fixed', 'both'
    percentageMarkup: 10, // Percentage to add to base fare
    fixedFee: 0, // Fixed fee to add
    discountPercent: 0, // Discount percentage (negative markup)
    // Commission settings
    commissionPercent: 0, // Commission the partner earns
    // Status
    status: 'approved',
    active: true,
  });

  // Filter states
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [partnerTypeFilter, setPartnerTypeFilter] = useState('all');

  // Check if user is admin
  const isAdmin = auth?.profile?.usertype === 'admin';

  // Fetch companies
  useEffect(() => {
    if (!firebase?.database) return;

    const companiesRef = firebase.database.ref('turpris_companies');
    companiesRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
        setCompanies(list);
      } else {
        setCompanies([]);
      }
      setLoading(false);
    });

    return () => companiesRef.off();
  }, [firebase]);

  // Fetch guest bookings
  useEffect(() => {
    if (!firebase?.database) return;

    const bookingsRef = firebase.database.ref('guest_bookings');
    bookingsRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setGuestBookings(list);
      } else {
        setGuestBookings([]);
      }
    });

    return () => bookingsRef.off();
  }, [firebase]);

  // Fetch users
  useEffect(() => {
    if (!firebase?.database) return;

    const usersRef = firebase.database.ref('users');
    usersRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ uid: id, ...value }));
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setUsers(list);
      } else {
        setUsers([]);
      }
    });

    return () => usersRef.off();
  }, [firebase]);

  // Fetch partners (hotels, companies)
  useEffect(() => {
    if (!firebase?.database) return;

    const partnersRef = firebase.database.ref('turpris_partners');
    partnersRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setPartners(list);
      } else {
        setPartners([]);
      }
    });

    return () => partnersRef.off();
  }, [firebase]);

  // Fetch partner users
  useEffect(() => {
    if (!firebase?.database) return;

    const partnerUsersRef = firebase.database.ref('turpris_partner_users');
    partnerUsersRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setPartnerUsers(list);
      } else {
        setPartnerUsers([]);
      }
    });

    return () => partnerUsersRef.off();
  }, [firebase]);

  // Company actions
  const handleApproveCompany = async (company) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_companies/${company.id}`).update({
        status: 'approved',
        approvedBy: auth?.profile?.uid,
        approvedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: 'Selskap godkjent!', severity: 'success' });
      setDetailDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleRejectCompany = async (company) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_companies/${company.id}`).update({
        status: 'rejected',
        rejectedBy: auth?.profile?.uid,
        rejectedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: 'Selskap avvist', severity: 'info' });
      setDetailDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleDeleteCompany = async (company) => {
    if (!firebase?.database) return;
    if (!window.confirm('Er du sikker på at du vil slette dette selskapet?')) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_companies/${company.id}`).remove();
      setSnackbar({ open: true, message: 'Selskap slettet', severity: 'info' });
      setDetailDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // User actions
  const handleApproveUser = async (user) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`users/${user.uid}`).update({
        approved: true,
        approvedBy: auth?.profile?.uid,
        approvedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: 'Bruker godkjent!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleBlockUser = async (user) => {
    if (!firebase?.database) return;
    const newStatus = !user.approved;
    setActionLoading(true);
    try {
      await firebase.database.ref(`users/${user.uid}`).update({
        approved: newStatus,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({
        open: true,
        message: newStatus ? 'Bruker aktivert' : 'Bruker blokkert',
        severity: newStatus ? 'success' : 'warning'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleUpdateUserType = async (user, newType) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`users/${user.uid}`).update({
        usertype: newType,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: 'Brukertype oppdatert!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Guest booking actions
  const handleUpdateBookingStatus = async (booking, newStatus) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`guest_bookings/${booking.id}`).update({
        status: newStatus,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: `Booking status oppdatert til: ${newStatus}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleDeleteBooking = async (booking) => {
    if (!firebase?.database) return;
    if (!window.confirm('Er du sikker på at du vil slette denne bookingen?')) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`guest_bookings/${booking.id}`).remove();
      setSnackbar({ open: true, message: 'Booking slettet', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Partner actions
  const handleTogglePartnerStatus = async (partner) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partners/${partner.id}`).update({
        active: !partner.active,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({
        open: true,
        message: partner.active ? 'Partner deaktivert' : 'Partner aktivert',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleDeletePartner = async (partner) => {
    if (!firebase?.database) return;
    if (!window.confirm('Er du sikker på at du vil slette denne partneren?')) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partners/${partner.id}`).remove();
      setSnackbar({ open: true, message: 'Partner slettet', severity: 'info' });
      setDetailDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleUpdatePartnerPricing = async (partner, pricingData) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partners/${partner.id}`).update({
        ...pricingData,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({ open: true, message: 'Prising oppdatert!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Generate unique partner code
  const generatePartnerCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!firebase?.database) return;
    if (!newUserForm.firstName || !newUserForm.email) {
      setSnackbar({ open: true, message: 'Fornavn og e-post er påkrevd', severity: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const newUserRef = firebase.database.ref('users').push();
      await newUserRef.set({
        firstName: newUserForm.firstName,
        lastName: newUserForm.lastName,
        email: newUserForm.email,
        mobile: newUserForm.mobile,
        usertype: newUserForm.usertype,
        approved: newUserForm.approved,
        createdAt: new Date().toISOString(),
        createdBy: auth?.profile?.uid,
        source: 'turpris_admin',
      });
      setSnackbar({ open: true, message: 'Bruker opprettet!', severity: 'success' });
      setCreateDialog(false);
      setNewUserForm({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        usertype: 'customer',
        approved: true,
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Create new company
  const handleCreateCompany = async () => {
    if (!firebase?.database) return;
    if (!newCompanyForm.name || !newCompanyForm.email) {
      setSnackbar({ open: true, message: 'Navn og e-post er påkrevd', severity: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const newCompanyRef = firebase.database.ref('turpris_companies').push();
      await newCompanyRef.set({
        ...newCompanyForm,
        submittedAt: new Date().toISOString(),
        submittedBy: auth?.profile?.uid,
        approvedBy: newCompanyForm.status === 'approved' ? auth?.profile?.uid : null,
        approvedAt: newCompanyForm.status === 'approved' ? new Date().toISOString() : null,
      });
      setSnackbar({ open: true, message: 'Selskap opprettet!', severity: 'success' });
      setCreateDialog(false);
      setNewCompanyForm({
        name: '',
        type: 'taxi',
        orgNumber: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        status: 'approved',
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Create new partner (hotel/company)
  const handleCreatePartner = async () => {
    if (!firebase?.database) return;
    if (!newPartnerForm.name || !newPartnerForm.email) {
      setSnackbar({ open: true, message: 'Navn og e-post er påkrevd', severity: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const partnerCode = generatePartnerCode();
      const newPartnerRef = firebase.database.ref('turpris_partners').push();
      await newPartnerRef.set({
        ...newPartnerForm,
        partnerCode: partnerCode,
        bookingUrl: `${window.location.origin}/turpris/landing?partner=${partnerCode}`,
        createdAt: new Date().toISOString(),
        createdBy: auth?.profile?.uid,
      });
      setSnackbar({ open: true, message: `Partner opprettet! Kode: ${partnerCode}`, severity: 'success' });
      setCreateDialog(false);
      setNewPartnerForm({
        name: '',
        type: 'hotel',
        orgNumber: '',
        contactName: '',
        email: '',
        phone: '',
        address: '',
        pricingType: 'percentage',
        percentageMarkup: 10,
        fixedFee: 0,
        discountPercent: 0,
        commissionPercent: 0,
        status: 'approved',
        active: true,
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Copy partner URL to clipboard
  const handleCopyPartnerUrl = (partner) => {
    const url = partner.bookingUrl || `${window.location.origin}/turpris/landing?partner=${partner.partnerCode}`;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'URL kopiert til utklippstavle!', severity: 'success' });
  };

  // Copy partner portal URL
  const handleCopyPartnerPortalUrl = (partner) => {
    const url = `${window.location.origin}/partner/${partner.partnerCode}`;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Partner portal URL kopiert!', severity: 'success' });
  };

  // Create partner user (receptionist/subadmin)
  const handleCreatePartnerUser = async () => {
    if (!firebase?.database) return;
    if (!newPartnerUserForm.username || !newPartnerUserForm.partnerId) {
      setSnackbar({ open: true, message: 'Brukernavn og partner er påkrevd', severity: 'error' });
      return;
    }

    // Check if username already exists
    const existingUser = partnerUsers.find(u => u.username === newPartnerUserForm.username);
    if (existingUser) {
      setSnackbar({ open: true, message: 'Brukernavn er allerede i bruk', severity: 'error' });
      return;
    }

    setActionLoading(true);
    try {
      const password = newPartnerUserForm.password || generatePassword();
      const partner = partners.find(p => p.id === newPartnerUserForm.partnerId);

      const newUserRef = firebase.database.ref('turpris_partner_users').push();
      await newUserRef.set({
        username: newPartnerUserForm.username,
        password: password, // In production, this should be hashed
        firstName: newPartnerUserForm.firstName,
        lastName: newPartnerUserForm.lastName,
        email: newPartnerUserForm.email,
        role: newPartnerUserForm.role,
        partnerId: newPartnerUserForm.partnerId,
        partnerCode: partner?.partnerCode,
        partnerName: partner?.name,
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: auth?.profile?.uid,
      });

      // Show credentials dialog
      setCreatedCredentials({
        username: newPartnerUserForm.username,
        password: password,
        portalUrl: `${window.location.origin}/partner/${partner?.partnerCode}`,
        partnerName: partner?.name,
        role: newPartnerUserForm.role,
      });
      setCredentialsDialog(true);

      setSnackbar({ open: true, message: 'Partner bruker opprettet!', severity: 'success' });
      setCreateDialog(false);
      setNewPartnerUserForm({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        role: 'user',
        partnerId: '',
        active: true,
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Toggle partner user status
  const handleTogglePartnerUserStatus = async (user) => {
    if (!firebase?.database) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partner_users/${user.id}`).update({
        active: !user.active,
        updatedBy: auth?.profile?.uid,
        updatedAt: new Date().toISOString(),
      });
      setSnackbar({
        open: true,
        message: user.active ? 'Bruker deaktivert' : 'Bruker aktivert',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Delete partner user
  const handleDeletePartnerUser = async (user) => {
    if (!firebase?.database) return;
    if (!window.confirm('Er du sikker på at du vil slette denne brukeren?')) return;
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partner_users/${user.id}`).remove();
      setSnackbar({ open: true, message: 'Bruker slettet', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Reset partner user password
  const handleResetPartnerUserPassword = async (user) => {
    if (!firebase?.database) return;
    const newPassword = generatePassword();
    setActionLoading(true);
    try {
      await firebase.database.ref(`turpris_partner_users/${user.id}`).update({
        password: newPassword,
        passwordResetAt: new Date().toISOString(),
        passwordResetBy: auth?.profile?.uid,
      });
      setCreatedCredentials({
        username: user.username,
        password: newPassword,
        portalUrl: `${window.location.origin}/partner/${user.partnerCode}`,
        partnerName: user.partnerName,
        role: user.role,
        isReset: true,
      });
      setCredentialsDialog(true);
      setSnackbar({ open: true, message: 'Passord tilbakestilt!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setActionLoading(false);
  };

  // Get partner users for a specific partner
  const getPartnerUsersForPartner = (partnerId) => {
    return partnerUsers.filter(u => u.partnerId === partnerId);
  };

  // Filter data
  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contactName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBookings = guestBookings.filter(b =>
    b.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.customer?.phone?.includes(searchTerm) ||
    b.pickup?.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.mobile?.includes(searchTerm);

    const matchesType = userTypeFilter === 'all' || u.usertype === userTypeFilter;
    const matchesApproval = approvalFilter === 'all' ||
      (approvalFilter === 'approved' && u.approved) ||
      (approvalFilter === 'pending' && !u.approved);

    return matchesSearch && matchesType && matchesApproval;
  });

  const filteredPartners = partners.filter(p => {
    const matchesSearch =
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.partnerCode?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = partnerTypeFilter === 'all' || p.type === partnerTypeFilter;

    return matchesSearch && matchesType;
  });

  // Stats
  const pendingCompanies = companies.filter(c => c.status === 'pending').length;
  const approvedCompanies = companies.filter(c => c.status === 'approved').length;
  const pendingBookings = guestBookings.filter(b => b.status === 'pending').length;
  const pendingUsers = users.filter(u => !u.approved).length;
  const totalDrivers = users.filter(u => u.usertype === 'driver').length;
  const activePartners = partners.filter(p => p.active).length;

  if (!isAdmin) {
    return (
      <Box p={4}>
        <Alert severity="error">Du har ikke tilgang til denne siden. Kun admin.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            TurPris Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administrer brukere, selskaper, partnere og gjestebookinger
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => { setCreateType('user'); setCreateDialog(true); }}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            Ny bruker
          </Button>
          <Button
            variant="contained"
            startIcon={<BusinessIcon />}
            onClick={() => { setCreateType('company'); setCreateDialog(true); }}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
          >
            Nytt selskap
          </Button>
          <Button
            variant="contained"
            startIcon={<HotelIcon />}
            onClick={() => { setCreateType('partner'); setCreateDialog(true); }}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            Ny partner
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <GroupIcon sx={{ color: '#3b82f6' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{users.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Brukere</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <DriverIcon sx={{ color: '#22c55e' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{totalDrivers}</Typography>
                  <Typography variant="caption" color="text.secondary">Sjåfører</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <HotelIcon sx={{ color: '#8b5cf6' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{activePartners}</Typography>
                  <Typography variant="caption" color="text.secondary">Partnere</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon sx={{ color: '#f59e0b' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{approvedCompanies}</Typography>
                  <Typography variant="caption" color="text.secondary">Selskaper</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <PendingIcon sx={{ color: '#ef4444' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{pendingUsers + pendingCompanies}</Typography>
                  <Typography variant="caption" color="text.secondary">Venter</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <QrCodeIcon sx={{ color: '#0ea5e9' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">{guestBookings.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Bookinger</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab
              label={
                <Badge badgeContent={pendingUsers} color="warning">
                  <Box sx={{ pr: 2 }}>Brukere</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={partners.length} color="secondary">
                  <Box sx={{ pr: 2 }}>Partnere</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={pendingCompanies} color="warning">
                  <Box sx={{ pr: 2 }}>Selskaper</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={pendingBookings} color="info">
                  <Box sx={{ pr: 2 }}>Bookinger</Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Search and Filters */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Søk..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ minWidth: 250 }}
          />
          {activeTab === 0 && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={userTypeFilter}
                  label="Type"
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">Alle</MenuItem>
                  <MenuItem value="customer">Kunde</MenuItem>
                  <MenuItem value="driver">Sjåfør</MenuItem>
                  <MenuItem value="fleetadmin">Flåteadmin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={approvalFilter}
                  label="Status"
                  onChange={(e) => setApprovalFilter(e.target.value)}
                >
                  <MenuItem value="all">Alle</MenuItem>
                  <MenuItem value="approved">Godkjent</MenuItem>
                  <MenuItem value="pending">Venter</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          {activeTab === 1 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={partnerTypeFilter}
                label="Type"
                onChange={(e) => setPartnerTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="hotel">Hotell</MenuItem>
                <MenuItem value="company">Bedrift</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="other">Annet</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bruker</TableCell>
                      <TableCell>Kontakt</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Registrert</TableCell>
                      <TableCell align="right">Handlinger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" py={4}>
                            Ingen brukere funnet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => {
                        const typeConfig = USERTYPE_CONFIG[user.usertype] || USERTYPE_CONFIG.customer;
                        const TypeIcon = typeConfig.icon;
                        return (
                          <TableRow key={user.uid} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ bgcolor: typeConfig.color, width: 36, height: 36 }}>
                                  {user.firstName?.[0] || user.email?.[0] || '?'}
                                </Avatar>
                                <Box>
                                  <Typography fontWeight="medium">
                                    {user.firstName} {user.lastName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {user.uid?.substring(0, 8)}...
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{user.email}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.mobile || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={<TypeIcon sx={{ fontSize: 16 }} />}
                                label={typeConfig.label}
                                sx={{ bgcolor: typeConfig.color + '20', color: typeConfig.color }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={user.approved ? 'Aktiv' : 'Venter'}
                                color={user.approved ? 'success' : 'warning'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('nb-NO') : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Se detaljer">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedItem(user);
                                    setDetailDialog(true);
                                  }}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              {!user.approved && (
                                <Tooltip title="Godkjenn">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApproveUser(user)}
                                  >
                                    <ApproveIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={user.approved ? 'Blokker' : 'Aktiver'}>
                                <IconButton
                                  size="small"
                                  color={user.approved ? 'warning' : 'success'}
                                  onClick={() => handleBlockUser(user)}
                                >
                                  {user.approved ? <BlockIcon /> : <ApproveIcon />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Partners Tab */}
            {activeTab === 1 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Partnerkode</TableCell>
                      <TableCell>Prising</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Handlinger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPartners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" py={4}>
                            Ingen partnere funnet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPartners.map((partner) => {
                        const typeConfig = PARTNER_TYPE_CONFIG[partner.type] || PARTNER_TYPE_CONFIG.other;
                        const TypeIcon = typeConfig.icon;
                        return (
                          <TableRow key={partner.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: typeConfig.color + '20',
                                  }}
                                >
                                  <TypeIcon sx={{ color: typeConfig.color }} />
                                </Box>
                                <Box>
                                  <Typography fontWeight="medium">{partner.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {partner.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={typeConfig.label}
                                sx={{ bgcolor: typeConfig.color + '20', color: typeConfig.color }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography fontWeight="bold" sx={{ fontFamily: 'monospace', bgcolor: '#f0f0f0', px: 1, py: 0.5, borderRadius: 1 }}>
                                  {partner.partnerCode}
                                </Typography>
                                <Tooltip title="Kopier booking URL">
                                  <IconButton size="small" onClick={() => handleCopyPartnerUrl(partner)}>
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Kopier portal URL">
                                  <IconButton size="small" onClick={() => handleCopyPartnerPortalUrl(partner)} color="primary">
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {getPartnerUsersForPartner(partner.id).length} brukere
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {partner.percentageMarkup > 0 && (
                                  <Chip
                                    size="small"
                                    icon={<PercentIcon sx={{ fontSize: 14 }} />}
                                    label={`+${partner.percentageMarkup}%`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mr: 0.5 }}
                                  />
                                )}
                                {partner.fixedFee > 0 && (
                                  <Chip
                                    size="small"
                                    icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                                    label={`+${partner.fixedFee} kr`}
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ mr: 0.5 }}
                                  />
                                )}
                                {partner.discountPercent > 0 && (
                                  <Chip
                                    size="small"
                                    label={`-${partner.discountPercent}% rabatt`}
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                                {!partner.percentageMarkup && !partner.fixedFee && !partner.discountPercent && (
                                  <Typography variant="caption" color="text.secondary">Standard</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={partner.active ? 'Aktiv' : 'Inaktiv'}
                                color={partner.active ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Legg til bruker">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedPartnerForUser(partner);
                                    setNewPartnerUserForm({ ...newPartnerUserForm, partnerId: partner.id });
                                    setCreateType('partnerUser');
                                    setCreateDialog(true);
                                  }}
                                >
                                  <PersonAddIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Se detaljer">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedItem(partner);
                                    setDetailDialog(true);
                                  }}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={partner.active ? 'Deaktiver' : 'Aktiver'}>
                                <IconButton
                                  size="small"
                                  color={partner.active ? 'warning' : 'success'}
                                  onClick={() => handleTogglePartnerStatus(partner)}
                                >
                                  {partner.active ? <BlockIcon /> : <ApproveIcon />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Slett">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeletePartner(partner)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Companies Tab */}
            {activeTab === 2 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Selskap</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Kontakt</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Dato</TableCell>
                      <TableCell align="right">Handlinger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompanies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" py={4}>
                            Ingen selskaper funnet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCompanies.map((company) => {
                        const typeConfig = TYPE_CONFIG[company.type] || TYPE_CONFIG.taxi;
                        const statusConfig = STATUS_CONFIG[company.status] || STATUS_CONFIG.pending;
                        const TypeIcon = typeConfig.icon;
                        return (
                          <TableRow key={company.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: typeConfig.color + '20',
                                  }}
                                >
                                  <TypeIcon sx={{ color: typeConfig.color }} />
                                </Box>
                                <Box>
                                  <Typography fontWeight="medium">{company.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {company.orgNumber || 'Ingen org.nr'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={typeConfig.label}
                                sx={{ bgcolor: typeConfig.color + '20', color: typeConfig.color }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{company.contactName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {company.email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={statusConfig.label}
                                color={statusConfig.color}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {company.submittedAt ? new Date(company.submittedAt).toLocaleDateString('nb-NO') : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Se detaljer">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedItem(company);
                                    setDetailDialog(true);
                                  }}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              {company.status === 'pending' && (
                                <>
                                  <Tooltip title="Godkjenn">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleApproveCompany(company)}
                                    >
                                      <ApproveIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Avvis">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRejectCompany(company)}
                                    >
                                      <RejectIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Slett">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteCompany(company)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Guest Bookings Tab */}
            {activeTab === 3 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking ID</TableCell>
                      <TableCell>Kunde</TableCell>
                      <TableCell>Rute</TableCell>
                      <TableCell>Pris</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Dato</TableCell>
                      <TableCell align="right">Handlinger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" py={4}>
                            Ingen bookinger funnet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBookings.map((booking) => (
                        <TableRow key={booking.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <QrCodeIcon color="primary" />
                              <Box>
                                <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                                  {booking.id}
                                </Typography>
                                {booking.partnerCode && (
                                  <Chip size="small" label={booking.partnerCode} color="secondary" sx={{ height: 18, fontSize: 10 }} />
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{booking.customer?.name || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {booking.customer?.phone || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                                <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                  {booking.pickup?.address?.substring(0, 25)}...
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                  {booking.dropoff?.address?.substring(0, 25)}...
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {booking.estimate ? (
                              <Typography fontWeight="bold" color="primary">
                                {booking.estimate.total} {booking.estimate.currency || 'kr'}
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={booking.status || 'pending'}
                              color={booking.status === 'completed' ? 'success' : booking.status === 'accepted' ? 'info' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {booking.createdAt ? new Date(booking.createdAt).toLocaleString('nb-NO', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Se detaljer">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedItem(booking);
                                  setDetailDialog(true);
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {booking.status === 'pending' && (
                              <Tooltip title="Marker som akseptert">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleUpdateBookingStatus(booking, 'accepted')}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Slett">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBooking(booking)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {activeTab === 0 ? 'Brukerdetaljer' : activeTab === 1 ? 'Partnerdetaljer' : activeTab === 2 ? 'Selskapsdetaljer' : 'Bookingdetaljer'}
        </DialogTitle>
        <DialogContent dividers>
          {/* User Details */}
          {selectedItem && activeTab === 0 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar sx={{
                      bgcolor: USERTYPE_CONFIG[selectedItem.usertype]?.color || '#666',
                      width: 56,
                      height: 56
                    }}>
                      {selectedItem.firstName?.[0] || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedItem.firstName} {selectedItem.lastName}
                      </Typography>
                      <Chip
                        size="small"
                        label={USERTYPE_CONFIG[selectedItem.usertype]?.label || selectedItem.usertype}
                        sx={{
                          bgcolor: (USERTYPE_CONFIG[selectedItem.usertype]?.color || '#666') + '20',
                          color: USERTYPE_CONFIG[selectedItem.usertype]?.color || '#666'
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">E-post</Typography>
                  <Typography>{selectedItem.email || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Telefon</Typography>
                  <Typography>{selectedItem.mobile || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    size="small"
                    label={selectedItem.approved ? 'Aktiv' : 'Venter godkjenning'}
                    color={selectedItem.approved ? 'success' : 'warning'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Bruker ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedItem.uid}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Endre brukertype</Typography>
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <Select
                      value={selectedItem.usertype || 'customer'}
                      onChange={(e) => handleUpdateUserType(selectedItem, e.target.value)}
                    >
                      <MenuItem value="customer">Kunde</MenuItem>
                      <MenuItem value="driver">Sjåfør</MenuItem>
                      <MenuItem value="fleetadmin">Flåteadmin</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Partner Details */}
          {selectedItem && activeTab === 1 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: (PARTNER_TYPE_CONFIG[selectedItem.type]?.color || '#666') + '20',
                    }}>
                      {React.createElement(PARTNER_TYPE_CONFIG[selectedItem.type]?.icon || BusinessIcon, {
                        sx: { fontSize: 40, color: PARTNER_TYPE_CONFIG[selectedItem.type]?.color || '#666' }
                      })}
                    </Box>
                    <Box>
                      <Typography variant="h6">{selectedItem.name}</Typography>
                      <Chip
                        size="small"
                        label={PARTNER_TYPE_CONFIG[selectedItem.type]?.label || selectedItem.type}
                        sx={{
                          bgcolor: (PARTNER_TYPE_CONFIG[selectedItem.type]?.color || '#666') + '20',
                          color: PARTNER_TYPE_CONFIG[selectedItem.type]?.color || '#666'
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Box>
                        <Typography variant="subtitle2">Partnerkode</Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {selectedItem.partnerCode}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopyPartnerUrl(selectedItem)}
                      >
                        Kopier URL
                      </Button>
                    </Box>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Kontaktinformasjon
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Kontaktperson</Typography>
                  <Typography>{selectedItem.contactName || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Telefon</Typography>
                  <Typography>{selectedItem.phone || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">E-post</Typography>
                  <Typography>{selectedItem.email || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Adresse</Typography>
                  <Typography>{selectedItem.address || '-'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Prisinnstillinger
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Prosentpåslag (+{selectedItem.percentageMarkup || 0}%)
                  </Typography>
                  <Slider
                    value={selectedItem.percentageMarkup || 0}
                    onChange={(e, val) => setSelectedItem({ ...selectedItem, percentageMarkup: val })}
                    min={0}
                    max={50}
                    step={1}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 10, label: '10%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ color: '#6366f1' }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fast tillegg (kr)"
                    type="number"
                    value={selectedItem.fixedFee || 0}
                    onChange={(e) => setSelectedItem({ ...selectedItem, fixedFee: parseInt(e.target.value) || 0 })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+</InputAdornment>,
                      endAdornment: <InputAdornment position="end">kr</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rabatt (%)"
                    type="number"
                    value={selectedItem.discountPercent || 0}
                    onChange={(e) => setSelectedItem({ ...selectedItem, discountPercent: parseInt(e.target.value) || 0 })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">-</InputAdornment>,
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Provisjon til partner (%)"
                    type="number"
                    value={selectedItem.commissionPercent || 0}
                    onChange={(e) => setSelectedItem({ ...selectedItem, commissionPercent: parseInt(e.target.value) || 0 })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText="Prosent av turpris som partneren får i provisjon"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleUpdatePartnerPricing(selectedItem, {
                      percentageMarkup: selectedItem.percentageMarkup,
                      fixedFee: selectedItem.fixedFee,
                      discountPercent: selectedItem.discountPercent,
                      commissionPercent: selectedItem.commissionPercent,
                    })}
                    disabled={actionLoading}
                    sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Lagre prisinnstillinger'}
                  </Button>
                </Grid>

                {/* Partner Users Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Brukere ({getPartnerUsersForPartner(selectedItem.id).length})
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => {
                        setSelectedPartnerForUser(selectedItem);
                        setNewPartnerUserForm({ ...newPartnerUserForm, partnerId: selectedItem.id });
                        setCreateType('partnerUser');
                        setDetailDialog(false);
                        setCreateDialog(true);
                      }}
                    >
                      Legg til bruker
                    </Button>
                  </Box>

                  {getPartnerUsersForPartner(selectedItem.id).length === 0 ? (
                    <Alert severity="info">Ingen brukere registrert for denne partneren ennå.</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Bruker</TableCell>
                            <TableCell>Rolle</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Handlinger</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getPartnerUsersForPartner(selectedItem.id).map((user) => {
                            const roleConfig = PARTNER_USER_ROLE_CONFIG[user.role] || PARTNER_USER_ROLE_CONFIG.user;
                            const RoleIcon = roleConfig.icon;
                            return (
                              <TableRow key={user.id}>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: roleConfig.color }}>
                                      {user.firstName?.[0] || user.username?.[0] || '?'}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {user.firstName} {user.lastName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        @{user.username}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    icon={<RoleIcon sx={{ fontSize: 14 }} />}
                                    label={roleConfig.label}
                                    sx={{ bgcolor: roleConfig.color + '20', color: roleConfig.color }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={user.active ? 'Aktiv' : 'Inaktiv'}
                                    color={user.active ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Tilbakestill passord">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleResetPartnerUserPassword(user)}
                                    >
                                      <KeyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={user.active ? 'Deaktiver' : 'Aktiver'}>
                                    <IconButton
                                      size="small"
                                      color={user.active ? 'warning' : 'success'}
                                      onClick={() => handleTogglePartnerUserStatus(user)}
                                    >
                                      {user.active ? <BlockIcon fontSize="small" /> : <ApproveIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Slett">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeletePartnerUser(user)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                {/* Portal URLs */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Lenker
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <QrCodeIcon color="primary" />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">Booking URL (for gjester)</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {selectedItem.bookingUrl || `${window.location.origin}/turpris/landing?partner=${selectedItem.partnerCode}`}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleCopyPartnerUrl(selectedItem)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      <LinkIcon color="success" />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">Partner Portal (for ansatte)</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {`${window.location.origin}/partner/${selectedItem.partnerCode}`}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleCopyPartnerPortalUrl(selectedItem)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Company Details */}
          {selectedItem && activeTab === 2 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Selskapsnavn</Typography>
                  <Typography variant="h6">{selectedItem.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Chip
                    label={TYPE_CONFIG[selectedItem.type]?.label || selectedItem.type}
                    size="small"
                    sx={{
                      bgcolor: (TYPE_CONFIG[selectedItem.type]?.color || '#666') + '20',
                      color: TYPE_CONFIG[selectedItem.type]?.color || '#666'
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={STATUS_CONFIG[selectedItem.status]?.label || selectedItem.status}
                    color={STATUS_CONFIG[selectedItem.status]?.color || 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Org.nummer</Typography>
                  <Typography>{selectedItem.orgNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Kontaktperson</Typography>
                  <Typography>{selectedItem.contactName || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">E-post</Typography>
                  <Typography>{selectedItem.email || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Booking Details */}
          {selectedItem && activeTab === 3 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Booking ID</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {selectedItem.id}
                  </Typography>
                </Grid>
                {selectedItem.partnerCode && (
                  <Grid item xs={12}>
                    <Chip label={`Partner: ${selectedItem.partnerCode}`} color="secondary" />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Kundenavn</Typography>
                  <Typography>{selectedItem.customer?.name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Telefon</Typography>
                  <Typography>{selectedItem.customer?.phone || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Fra</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
                    <Typography>{selectedItem.pickup?.address || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Til</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                    <Typography>{selectedItem.dropoff?.address || '-'}</Typography>
                  </Box>
                </Grid>
                {selectedItem.estimate && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="primary.dark">Estimert pris</Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary.dark">
                        {selectedItem.estimate.total} {selectedItem.estimate.currency || 'kr'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Lukk</Button>
        </DialogActions>
      </Dialog>

      {/* Create User/Company/Partner Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createType === 'user' ? 'Opprett ny bruker' :
           createType === 'company' ? 'Opprett nytt selskap' :
           createType === 'partnerUser' ? 'Opprett partner bruker' :
           'Opprett ny partner'}
        </DialogTitle>
        <DialogContent dividers>
          {createType === 'user' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fornavn *"
                  value={newUserForm.firstName}
                  onChange={(e) => setNewUserForm({ ...newUserForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Etternavn"
                  value={newUserForm.lastName}
                  onChange={(e) => setNewUserForm({ ...newUserForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-post *"
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={newUserForm.mobile}
                  onChange={(e) => setNewUserForm({ ...newUserForm, mobile: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Brukertype</InputLabel>
                  <Select
                    value={newUserForm.usertype}
                    label="Brukertype"
                    onChange={(e) => setNewUserForm({ ...newUserForm, usertype: e.target.value })}
                  >
                    <MenuItem value="customer">Kunde</MenuItem>
                    <MenuItem value="driver">Sjåfør</MenuItem>
                    <MenuItem value="fleetadmin">Flåteadmin</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newUserForm.approved}
                      onChange={(e) => setNewUserForm({ ...newUserForm, approved: e.target.checked })}
                    />
                  }
                  label="Godkjent / Aktiv"
                />
              </Grid>
            </Grid>
          )}

          {createType === 'company' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Selskapsnavn *"
                  value={newCompanyForm.name}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newCompanyForm.type}
                    label="Type"
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, type: e.target.value })}
                  >
                    <MenuItem value="taxi">Taxi</MenuItem>
                    <MenuItem value="rideshare">Rideshare</MenuItem>
                    <MenuItem value="vip">VIP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Org.nummer"
                  value={newCompanyForm.orgNumber}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, orgNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-post *"
                  type="email"
                  value={newCompanyForm.email}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, email: e.target.value })}
                />
              </Grid>
            </Grid>
          )}

          {createType === 'partner' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Partnernavn *"
                  value={newPartnerForm.name}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, name: e.target.value })}
                  placeholder="f.eks. Thon Hotel Oslo"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newPartnerForm.type}
                    label="Type"
                    onChange={(e) => setNewPartnerForm({ ...newPartnerForm, type: e.target.value })}
                  >
                    <MenuItem value="hotel">Hotell</MenuItem>
                    <MenuItem value="company">Bedrift</MenuItem>
                    <MenuItem value="restaurant">Restaurant</MenuItem>
                    <MenuItem value="other">Annet</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Org.nummer"
                  value={newPartnerForm.orgNumber}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, orgNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kontaktperson"
                  value={newPartnerForm.contactName}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, contactName: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E-post *"
                  type="email"
                  value={newPartnerForm.email}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={newPartnerForm.phone}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={newPartnerForm.address}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, address: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Prisinnstillinger
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Prosentpåslag"
                  type="number"
                  value={newPartnerForm.percentageMarkup}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, percentageMarkup: parseInt(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+</InputAdornment>,
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Tillegg til basispris"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fast tillegg"
                  type="number"
                  value={newPartnerForm.fixedFee}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, fixedFee: parseInt(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+</InputAdornment>,
                    endAdornment: <InputAdornment position="end">kr</InputAdornment>,
                  }}
                  helperText="Fast beløp per tur"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Rabatt"
                  type="number"
                  value={newPartnerForm.discountPercent}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, discountPercent: parseInt(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">-</InputAdornment>,
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Rabatt til kunden"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Provisjon"
                  type="number"
                  value={newPartnerForm.commissionPercent}
                  onChange={(e) => setNewPartnerForm({ ...newPartnerForm, commissionPercent: parseInt(e.target.value) || 0 })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Partner får i provisjon"
                />
              </Grid>
            </Grid>
          )}

          {createType === 'partnerUser' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Oppretter bruker for: <strong>{partners.find(p => p.id === newPartnerUserForm.partnerId)?.name || 'Ukjent partner'}</strong>
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Partner *</InputLabel>
                  <Select
                    value={newPartnerUserForm.partnerId}
                    label="Partner *"
                    onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, partnerId: e.target.value })}
                  >
                    {partners.filter(p => p.active).map((partner) => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.name} ({partner.partnerCode})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Brukernavn *"
                  value={newPartnerUserForm.username}
                  onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  placeholder="resepsjon1"
                  helperText="Brukes til innlogging"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Passord"
                  value={newPartnerUserForm.password}
                  onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, password: e.target.value })}
                  placeholder="Auto-generert"
                  helperText="La stå tomt for auto-generert"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fornavn"
                  value={newPartnerUserForm.firstName}
                  onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Etternavn"
                  value={newPartnerUserForm.lastName}
                  onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-post"
                  type="email"
                  value={newPartnerUserForm.email}
                  onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Rolle *</InputLabel>
                  <Select
                    value={newPartnerUserForm.role}
                    label="Rolle *"
                    onChange={(e) => setNewPartnerUserForm({ ...newPartnerUserForm, role: e.target.value })}
                  >
                    <MenuItem value="user">
                      <Box>
                        <Typography>Bruker</Typography>
                        <Typography variant="caption" color="text.secondary">Kan booke turer for gjester</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="subadmin">
                      <Box>
                        <Typography>Sub-admin</Typography>
                        <Typography variant="caption" color="text.secondary">Kan se provisjon og justere priser</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={
              createType === 'user' ? handleCreateUser :
              createType === 'company' ? handleCreateCompany :
              createType === 'partnerUser' ? handleCreatePartnerUser :
              handleCreatePartner
            }
            disabled={actionLoading}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Opprett'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog - Shows after creating partner user */}
      <Dialog open={credentialsDialog} onClose={() => setCredentialsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createdCredentials?.isReset ? 'Passord tilbakestilt' : 'Bruker opprettet!'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Lagre disse opplysningene! Passordet vil ikke vises igjen.
          </Alert>

          {createdCredentials && (
            <Box>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Partner</Typography>
                    <Typography variant="h6">{createdCredentials.partnerName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Brukernavn</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{createdCredentials.username}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Passord</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', bgcolor: '#fff3cd', px: 1, borderRadius: 1 }}>
                      {createdCredentials.password}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Portal URL</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {createdCredentials.portalUrl}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Rolle</Typography>
                    <Chip
                      size="small"
                      label={createdCredentials.role === 'subadmin' ? 'Sub-admin' : 'Bruker'}
                      color={createdCredentials.role === 'subadmin' ? 'warning' : 'primary'}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  const text = `Partner: ${createdCredentials.partnerName}\nBrukernavn: ${createdCredentials.username}\nPassord: ${createdCredentials.password}\nPortal: ${createdCredentials.portalUrl}`;
                  navigator.clipboard.writeText(text);
                  setSnackbar({ open: true, message: 'Kopiert til utklippstavle!', severity: 'success' });
                }}
              >
                Kopier alle opplysninger
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialog(false)} variant="contained">
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Alert
        severity={snackbar.severity}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          display: snackbar.open ? 'flex' : 'none',
          zIndex: 9999,
        }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        {snackbar.message}
      </Alert>
    </Box>
  );
}
